import { useTheme } from "@emotion/react";
import {
  ActionIcon,
  Divider,
  Flex,
  Grid,
  rem,
  Text,
  Tooltip,
} from "@mantine/core";
import { IconCopy, IconListTree } from "@tabler/icons-react";
import { Fragment, useState } from "react";
import { useDispatch } from "react-redux";
import { CheckListType } from "src/components/pages/Settings/Miscellaneous/CheckLists/CheckLists.types";
import { StyledGridCol } from "src/components/pages/Summary/VerifyEntries/VerifyEntries.styled";
import { getCheckListProgress } from "src/generator/utils/getCheckListProgress";
import {
  type ICheckList,
  type ICheckListItem,
  type SetChecklist,
} from "src/interfaces";
import { setEditing } from "src/redux/slices/entrySlice";
import { setCheckList as setStateChecklist } from "src/redux/slices/generatorSlice";
import { Notification } from "src/utils/Notification";
import { v4 as uuidv4 } from "uuid";

interface Props {
  row: ICheckList;
  setCheckList: SetChecklist;
  disableCopy: boolean;
  disabled: boolean;
}

const ChecklistListRow = ({
  row,
  setCheckList,
  disableCopy,
  disabled,
}: Props) => {
  const colors = useTheme();
  const dispatch = useDispatch();
  const [itemsVisible, setItemsVisible] = useState(false);
  const handleCopy = async (e: any) => {
    e.stopPropagation();
    if (disableCopy || disabled) return;
    const { sort, data, suffix } = row;

    const itemMapper = (el: ICheckListItem) => ({
      text: el.text,
      unit: el.unit,
      naAvailable: el.naAvailable ?? false,
    });

    let clearedItems = [];
    if (data.type === CheckListType.Single)
      clearedItems = data.list.map(itemMapper);
    else {
      clearedItems = data.list.map((sub: any) => {
        const items = sub.items?.map(itemMapper);
        return { ...sub, items };
      });
    }

    dispatch(
      setStateChecklist({
        ...row,
        data: { ...data, list: clearedItems },
        suffix: suffix + 1,
        sort: sort + 1,
        cid: uuidv4(),
      }),
    );

    dispatch(setEditing(false));
    setTimeout(() => dispatch(setEditing(true)), 100);
    Notification("Success", "Check List has been copied successfully", "green");
  };

  const handleClick = () => {
    if (disabled) return;
    row.data.type === CheckListType.Group
      ? setItemsVisible(!itemsVisible)
      : setCheckList({ ...row, selectedItemIndex: null });
  };

  return (
    <Grid
      onClick={handleClick}
      sx={{
        border: `2px solid ${colors.colors.blue[9]}`,
        borderRadius: 12,
        backgroundColor: colors.colors.background[0],
        height: "auto",
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? "not-allowed" : "pointer",
      }}>
      <StyledGridCol justify="" align="center" span={8}>
        {row.name} {row.suffix !== 1 ? `(${row.suffix})` : ""}
      </StyledGridCol>

      <StyledGridCol justify="" align="center" span={1}>
        {row.version}
      </StyledGridCol>
      <StyledGridCol justify="" align="center" span={2}>
        <Flex w="100%" justify="end" align="center" gap="sm">
          <Text size="sm">
            {row.data.type !== CheckListType.Group &&
              getCheckListProgress(row.data.list)}
          </Text>
          {row.data.type === CheckListType.Group && (
            <Tooltip label="Show group items" position="bottom" withArrow>
              <Flex align="center">
                <Text size="sm">
                  ({row.data.list.filter(el => !el.hidden).length})
                </Text>
                <ActionIcon
                  color="accent"
                  onClick={e => {
                    e.stopPropagation();
                    setItemsVisible(!itemsVisible);
                  }}>
                  <IconListTree />
                </ActionIcon>
              </Flex>
            </Tooltip>
          )}
          {!window.api && (
            <Tooltip
              label="Copy"
              position="bottom"
              withArrow
              disabled={disabled}>
              <ActionIcon
                color="accent"
                onClick={handleCopy}
                style={{
                  display: disableCopy ? "none" : "block",
                  cursor: disabled ? "not-allowed" : "pointer",
                }}>
                <IconCopy />
              </ActionIcon>
            </Tooltip>
          )}
        </Flex>
      </StyledGridCol>
      {row.data.type === CheckListType.Group && itemsVisible && (
        <>
          <Grid.Col
            span={12}
            style={{
              padding: "0px 20px",
              boxSizing: "border-box",
            }}>
            <Divider my="sm" label="Items" size="sm" />
          </Grid.Col>
          {row.data.list.map(
            (sub, i) =>
              !sub.hidden && (
                <Grid.Col
                  span={12}
                  sx={{ padding: "0px 20px" }}
                  key={`checklist-item-${i}`}
                  onClick={() => {
                    setCheckList({ ...row, selectedItemIndex: i });
                  }}>
                  <Grid
                    sx={{
                      border: `2px solid ${colors.colors.blue[9]}`,
                      borderRadius: 12,
                      marginBottom: rem(15),
                      backgroundColor: colors.colors.tdBackgroundColor[0],
                    }}>
                    <Grid.Col
                      span={10}
                      style={{
                        backgroundColor: colors.colors.tdBackgroundColor[0],
                        borderTopLeftRadius: 12,
                        borderBottomLeftRadius: 12,
                      }}>
                      <p
                        style={{
                          fontSize: "14px",
                          overflowY: "auto",
                        }}>
                        {sub?.name + (sub?.desc ? " - " + sub?.desc : "")}
                      </p>
                    </Grid.Col>
                    <Grid.Col
                      span={2}
                      style={{
                        backgroundColor: colors.colors.tdBackgroundColor[0],
                        borderTopRightRadius: 12,
                        borderBottomRightRadius: 12,
                      }}>
                      <Flex align="center">
                        <p
                          style={{
                            fontSize: "14px",
                            maxHeight: "50px",
                            overflowY: "auto",
                          }}>
                          {getCheckListProgress(
                            (sub as unknown as { items: any[] }).items,
                          )}
                        </p>
                      </Flex>
                    </Grid.Col>
                  </Grid>
                </Grid.Col>
              ),
          )}
        </>
      )}
    </Grid>
  );
};

export default ChecklistListRow;
